import React, { useContext, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { AuthContext } from 'components/authentication/context/authContext';
import { useAccount, useAccountTypes } from 'store/registry/useAccountManagement';
import { useAccountBalance } from 'store/registry/useAccountBalance';
import { useMovementOperations } from 'store/registry/useMovementOperations';
import { LedgerMovement } from 'store/registry/useLedgerMovements';
import MovementsTable from './MovementsTable';
import MovementFormDrawer from './MovementFormDrawer';
import CsvImportDialog from './CsvImportDialog';
import LedgerLinkDialog from './LedgerLinkDialog';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { useQueryClient } from '@tanstack/react-query';
import intl from 'components/i18n/ReactIntlWrapper';

const AccountDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userEntityDetails } = useContext(AuthContext);
  const accountId = parseInt(id || '0', 10);

  // Fetch account data
  const { data: account, isLoading: accountLoading, error: accountError } = useAccount(accountId);
  const { data: accountTypes = [] } = useAccountTypes();
  const { 
    data: balance, 
    isLoading: balanceLoading, 
    error: balanceError 
  } = useAccountBalance(accountId, !!account);

  // Find account type for display
  const accountTypeData = accountTypes.find(at => at.id === account?.accountTypeId);

  // Movement form state
  const [movementDrawerOpen, setMovementDrawerOpen] = useState(false);
  const [selectedMovement, setSelectedMovement] = useState<any>(null);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [movementToDelete, setMovementToDelete] = useState<LedgerMovement | null>(null);
  const { deleteMovement } = useMovementOperations();

  // CSV import state
  const [csvImportOpen, setCsvImportOpen] = useState(false);

  // Ledger link state
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [movementToLink, setMovementToLink] = useState<LedgerMovement | null>(null);
  const api: Api = useApi();
  const { success: toastSuccess, error: toastError } = useToastMessageQueue();
  const queryClient = useQueryClient();

  const handleBackClick = () => {
    navigate('/accounts');
  };

  const handleNewMovement = () => {
    setSelectedMovement(null);
    setMovementDrawerOpen(true);
  };

  const handleEditMovement = (movement: LedgerMovement) => {
    setSelectedMovement(movement);
    setMovementDrawerOpen(true);
  };

  const handleDeleteMovement = (movement: LedgerMovement) => {
    setMovementToDelete(movement);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!movementToDelete) return;
    deleteMovement.mutate(
      { id: movementToDelete.id, accountId },
      {
        onSuccess: () => setDeleteDialogOpen(false),
        onSettled: () => setMovementToDelete(null),
      }
    );
  };

  const handleMovementDrawerClose = () => {
    setMovementDrawerOpen(false);
    setSelectedMovement(null);
  };

  const handleLinkMovement = (movement: LedgerMovement) => {
    setMovementToLink(movement);
    setLinkDialogOpen(true);
  };

  const handleReconcileMovement = (movement: LedgerMovement) => {
    navigate(`/account/detail/${accountId}/reconcile/${movement.id}`);
  };

  const handleConfirmLink = async (targetSeatId: number) => {
    if (!movementToLink) return;
    try {
      await api.invoiceApiWrapper.linkLedgerEntries('1', movementToLink.id, targetSeatId);
      queryClient.invalidateQueries({ queryKey: ['ledgerMovements', accountId] });
      toastSuccess({ message: intl.get('successcodes.entriesLinked') || 'Entries linked successfully.' });
      setLinkDialogOpen(false);
      setMovementToLink(null);
    } catch {
      toastError({ message: intl.get('errorcodes.errorLinkingEntries') || 'Failed to link entries.' });
    }
  };

  const handleConfirmUnlink = async () => {
    if (!movementToLink) return;
    try {
      await api.invoiceApiWrapper.unlinkLedgerEntries('1', movementToLink.id);
      queryClient.invalidateQueries({ queryKey: ['ledgerMovements', accountId] });
      toastSuccess({ message: intl.get('successcodes.entriesUnlinked') || 'Link removed successfully.' });
      setLinkDialogOpen(false);
      setMovementToLink(null);
    } catch {
      toastError({ message: intl.get('errorcodes.errorUnlinkingEntries') || 'Failed to remove link.' });
    }
  };

  const formatCurrency = (amount: number, currencyCode = 'USD') => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode
      }).format(amount);
    } catch (error) {
      return `${currencyCode} ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  };

  const getBalanceColor = (balance: number) => {
    if (balance > 0) return 'success';
    if (balance < 0) return 'error';
    return 'default';
  };

  const getBalanceIcon = (balance: number) => {
    if (balance > 0) return 'mdi:trending-up';
    if (balance < 0) return 'mdi:trending-down';
    return 'mdi:minus';
  };

  // Loading state
  if (accountLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Error state
  if (accountError || !account) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert 
          severity="error" 
          action={
            <Button onClick={handleBackClick}>
              {intl.get('accountDetail.backToAccounts')}
            </Button>
          }
        >
          {intl.get('common.errorLoadingData')}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={3}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6" color="text.secondary" display="flex" alignItems="center" gap={1}>
              <Icon icon="mdi:wallet" width={20} />
              {account.accountName} ({account.accountNumber})
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Icon icon="mdi:arrow-left" />}
            onClick={handleBackClick}
          >
            {intl.get('common.back')}
          </Button>
        </Box>
      </Box>

      {/* Account Information and Balance Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Account Information Card */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <Icon icon="mdi:information" width={20} />
                {intl.get('accountManagement.accountInformation')}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {intl.get('accountManagement.accountName')}
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {account.accountName}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {intl.get('accountManagement.accountNumber')}
                  </Typography>
                  <Typography variant="body1" fontFamily="monospace" fontWeight="500">
                    {account.accountNumber}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {intl.get('accountManagement.typeAndCategory')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Chip 
                      label={accountTypeData?.displayName || account.accountType}
                      size="small"
                    />
                    {accountTypeData?.categoryDisplayName && (
                      <Chip 
                        label={accountTypeData.categoryDisplayName}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {intl.get('accountManagement.status')}
                  </Typography>
                  <Chip 
                    label={account.isActive 
                      ? intl.get('accountManagement.active') 
                      : intl.get('accountManagement.inactive')
                    }
                    color={account.isActive ? 'success' : 'error'}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>

                {account.description && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {intl.get('accountManagement.description')}
                    </Typography>
                    <Typography variant="body1">
                      {account.description}
                    </Typography>
                  </Box>
                )}

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {intl.get('accountManagement.currency')}
                  </Typography>
                  <Chip 
                    label={account.currency}
                    size="small" 
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Balance Information Card */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <Icon icon="mdi:scale-balance" width={20} />
                {intl.get('accountManagement.balanceInformation')}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {balanceLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : balanceError ? (
                <Box textAlign="center" py={4}>
                  <Icon icon="mdi:alert-circle" width={48} color="error" />
                  <Typography variant="body2" color="error" mt={1}>
                    {intl.get('accountManagement.errorLoadingBalance')}
                  </Typography>
                </Box>
              ) : balance ? (
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {intl.get('accountManagement.currentBalance')}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                      <Icon 
                        icon={getBalanceIcon(balance.balance)}
                        width={20}
                        color={getBalanceColor(balance.balance) === 'success' ? '#4caf50' : 
                              getBalanceColor(balance.balance) === 'error' ? '#f44336' : '#757575'}
                      />
                      <Typography 
                        variant="h5" 
                        color={getBalanceColor(balance.balance) === 'success' ? 'success.main' : 
                              getBalanceColor(balance.balance) === 'error' ? 'error.main' : 'text.primary'}
                        fontWeight="bold"
                      >
                        {formatCurrency(balance.balance, balance.currency)}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {intl.get('accountManagement.totalIncoming')}
                    </Typography>
                    <Typography variant="h6" color="success.main" fontFamily="monospace">
                      {formatCurrency(balance.totalIncoming || 0, balance.currency)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {intl.get('accountManagement.totalOutgoing')}
                    </Typography>
                    <Typography variant="h6" color="error.main" fontFamily="monospace">
                      {formatCurrency(balance.totalOutgoing || 0, balance.currency)}
                    </Typography>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {intl.get('accountManagement.entryCount')}
                    </Typography>
                    <Typography variant="body1">
                      {balance.entryCount} {intl.get('accountManagement.transactions')}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {intl.get('accountManagement.asOfDate')}
                    </Typography>
                    <Typography variant="body2" fontFamily="monospace">
                      {balance.asOfDate ? new Date(balance.asOfDate).toLocaleString() : intl.get('common.notAvailable')}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box textAlign="center" py={4}>
                  <Icon icon="mdi:database-off" width={48} color="text.secondary" />
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    {intl.get('accountManagement.noBalanceData')}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Movements Table */}
      <Box sx={{ mt: 4 }}>
        <MovementsTable
          accountId={accountId}
          accountCurrency={account.currency}
          onNewMovement={handleNewMovement}
          onEditMovement={handleEditMovement}
          onDeleteMovement={handleDeleteMovement}
          onImportCsv={() => setCsvImportOpen(true)}
          onLinkMovement={handleLinkMovement}
          onReconcileMovement={handleReconcileMovement}
        />
      </Box>

      {/* Movement Form Drawer */}
      <MovementFormDrawer
        open={movementDrawerOpen}
        movement={selectedMovement}
        accountId={accountId}
        accountCurrency={account?.currency}
        onClose={handleMovementDrawerClose}
        onSuccess={() => {
          handleMovementDrawerClose();
        }}
      />

      {/* Ledger Link Dialog */}
      <LedgerLinkDialog
        open={linkDialogOpen}
        movement={movementToLink}
        onConfirmLink={handleConfirmLink}
        onConfirmUnlink={handleConfirmUnlink}
        onCancel={() => { setLinkDialogOpen(false); setMovementToLink(null); }}
      />

      {/* CSV Import Dialog */}
      <CsvImportDialog
        open={csvImportOpen}
        accountId={accountId}
        onClose={() => setCsvImportOpen(false)}
        onSuccess={() => setCsvImportOpen(false)}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{intl.get('movements.deleteTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {intl.get('movements.deleteConfirm')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleteMovement.isPending}
          >
            {intl.get('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteMovement.isPending}
            startIcon={deleteMovement.isPending ? <CircularProgress size={16} /> : <Icon icon="mdi:trash-can-outline" />}
          >
            {intl.get('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AccountDetail;